import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useMediaQuery } from '@material-ui/core';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';

import { BackLink } from '../../components/atoms';
import { Hidden, LoadingSpinner, Page } from '../../components/layouts';
import { CheckoutButton } from '../../components/molecules';
import { Basket } from '../../components/organisms';
import { CampaignConsentDialog, Menu } from '../../components/partials';
import { PageHeader } from '../../components/partials/PageHeader';
import { analyticsService } from '../../core/services';
import { BasketActionsType } from '../../types/basket';
import { MenuActionsType } from '../../types/menu.d';
import { domUtils, getUtils, menuUtils, urlUtils } from '../../utils';
import { usePrevious } from '../../utils/hooks';
import { Track } from '../../weborder/analytics';
import * as Api from '../../weborder/Api';
import * as BasketControl from '../../weborder/BasketControl';
import {
  DispatchProps,
  LoadStatus,
  StateProps,
} from '../../weborder/component/Restaurant/index';
import { OutsideDeliveryZone, RouteParams } from '../../weborder/Routes';
import { Utilities } from '../../weborder/Utilities';
import {
  MenuDetailDialog,
  MenuOptionsDialog,
  NotAvailableWarning,
  ProfilePanel,
  StoreHeader,
} from './partials';
import { restaurantMenuUtils } from './utils';
import {
  checkIsCollectionRestaurantOnly,
  checkIsTableServiceOnly,
} from './utils/locationHeaderUtils';

type Props = StateProps & DispatchProps & RouteParams;

const BASKET_HEADER_AND_BUTTON = 150;
const BASKET_TOP_MARGIN = 30;
export const NAV_BAR_HEIGHT = 50;

const useStyles = (viewportTopOffset) =>
  makeStyles((theme: Theme) => {
    const maxHeightOffset =
      viewportTopOffset + BASKET_HEADER_AND_BUTTON + NAV_BAR_HEIGHT;
    return {
      [theme.breakpoints.up('lg')]: {
        floatingBasket: {
          maxHeight: `calc(100vh - ${viewportTopOffset}px)`,
          overflow: 'hidden',
          position: 'sticky',
          top: viewportTopOffset,

          '&.hasNavigation': {
            top: `${viewportTopOffset + NAV_BAR_HEIGHT}px`,

            '& .basketItems': {
              maxHeight: `calc(100vh - ${maxHeightOffset}px)`,
              overflowY: 'auto',
            },
          },

          '& .basketOutline': {
            border: `1px solid ${theme.palette.grey[400]}`,
            borderBottom: 'none',
          },

          '& .basketItems': {
            maxHeight: `calc(100vh - ${
              BASKET_HEADER_AND_BUTTON + viewportTopOffset
            }px)`,
            overflowY: 'auto',
          },
        },
      },
    };
  })();

export function RestaurantMenu(props: Props): JSX.Element {
  const {
    AddItemToBasket: addItemToBasket,
    AddToExpandedState: addToExpandedState,
    AppConfig: appConfig,
    Basket: basket,
    Customer: customer,
    DeliveryLocation: deliveryLocation,
    EditTableNumber: editTableNumber,
    ExpandedState: expandedState,
    FindInitialRestaurant: findInitialRestaurant,
    getLocationAreaData,
    getMenuByUrl,
    getRestaurantData,
    InitializeExpandedState: initializeExpandedState,
    Languages: languages,
    location,
    menu,
    menuUrl,
    params,
    PushLoginUrl: pushLoginUrl,
    RemoveFromExpandedState: removeFromExpandedState,
    RemoveItemFromBasket: removeItemFromBasket,
    restaurant,
    Restaurants: restaurants,
    SetRestaurantUrl: setRestaurantUrl,
    setTableLock,
    translate,
  } = props;

  const { type } = params;
  const concessionStoreId = Number(params.concessionStoreId);
  const id = Number(params.id);
  const vrId = Number(params.vrid);

  const { enqueueSnackbar } = useSnackbar();

  const [showMenuDetailDialog, setShowMenuDetailDialog] = useState(false);
  const [showMenuOptionsDialog, setShowMenuOptionsDialog] = useState(false);
  const [selectedMenuItem, setSelectedMenuOption] = useState(null);
  const [hasLockedTableNumber, setHasLockedTableNumber] = useState(false);

  const restaurantListLoadStatus = useRef<LoadStatus>(
    restaurants.status === 'success' ? 'LOADED' : 'NOT_LOADED',
  );
  const restaurantDetailLoadStatus = useRef<LoadStatus>(
    restaurant.status === 'success' ? 'LOADED' : 'NOT_LOADED',
  );
  const menuLoadStatus = useRef<LoadStatus>('NOT_LOADED');

  const prevMenuUrl = usePrevious(menuUrl);

  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme?.breakpoints.down('xs'));
  const menuHasConcessions: boolean = menu && menu?.ConcessionStores.length > 0;

  const viewportTopOffset = menuUtils.getViewportTopOffset(isMobileScreen);
  const heightOfConcessionNav = 65;
  const offsetForConcession = menuHasConcessions ? heightOfConcessionNav : 0;

  const offsetTop = viewportTopOffset + BASKET_TOP_MARGIN + offsetForConcession;
  const classes = useStyles(offsetTop);
  const languageCode = getUtils.activeLanguageCode(languages);

  const concessionStoreIdPath = concessionStoreId
    ? `/${concessionStoreId}`
    : '';

  /* TODO - create custom hooks to handle high volume of useEffects
   possible hooks - useMenu, useRestaurantDetails, etc 
   can possibly be shared with OrderCheckout */
  useEffect(() => {
    if (
      restaurant.status === 'success' &&
      restaurantDetailLoadStatus.current === 'LOADED'
    ) {
      domUtils.skipToContent();
    }
  }, [restaurant.status]);

  useEffect(() => {
    setRestaurantUrl();
  }, []);

  useEffect(() => {
    const saveServiceLocation = () => {
      const locationQuery = new URLSearchParams(location.search).get(
        'location_id',
      );
      const locationId = parseInt(locationQuery);

      if (Number.isInteger(locationId)) {
        editTableNumber(vrId, locationId);
        setHasLockedTableNumber(true);
      }
    };

    saveServiceLocation();
  }, []);

  useEffect(() => {
    if (
      restaurant.status !== 'success' &&
      restaurantDetailLoadStatus.current === 'NOT_LOADED'
    ) {
      getRestaurantData(vrId, type, id);
      restaurantDetailLoadStatus.current = 'LOADING';
    }
  }, [restaurant.status]);

  useEffect(() => {
    if (restaurant.status === 'success') {
      restaurantDetailLoadStatus.current = 'LOADED';
      getLocationAreaData(id);
    }
  }, [restaurant.status]);

  useEffect(() => {
    if (
      restaurants.status !== 'success' &&
      restaurantListLoadStatus.current === 'NOT_LOADED'
    ) {
      findInitialRestaurant();
      restaurantListLoadStatus.current = 'LOADING';
    }
  }, [restaurants.status]);

  useEffect(() => {
    if (restaurants.status === 'success') {
      restaurantListLoadStatus.current = 'LOADED';
    }
  }, [restaurants.status]);

  useEffect(() => {
    const isMenuNotLoaded =
      !menu && menuUrl && menuLoadStatus.current === 'NOT_LOADED';
    const hasMenuUpdated = prevMenuUrl && prevMenuUrl !== menuUrl;

    if (isMenuNotLoaded || hasMenuUpdated) {
      getMenuByUrl(menuUrl);
      menuLoadStatus.current = 'LOADING';
    }
  }, [menu, menuUrl]);

  useEffect(() => {
    if (menu) {
      menuLoadStatus.current = 'LOADED';
    }
  }, [menu]);

  useEffect(() => {
    if (appConfig.status === 'success' && restaurant.status === 'success') {
      const trackUserProperties = (): void => {
        const userId = customer.status === 'success' ? customer.data.UserId : 0;
        const whiteLabelId = appConfig.data.WhiteLabelId;

        const eventData = {
          amp_userProperties: {
            channel: 'Web',
            PRID: id,
            userId,
            WLID: whiteLabelId,
          },
        };
        analyticsService.track(eventData);
      };

      trackUserProperties();
    }
  }, [appConfig.status, customer.status, restaurant.status]);

  const handleOnAddItem = (payload: BasketControl.Item): void => {
    Object.assign(payload.Item, {
      ConcessionStoreId: Number(params?.concessionStoreId),
    });
    addItemToBasket(payload);
  };

  const handleOnRemoveItem = (payload: {
    menuId: number;
    index: number;
    indexName?: string;
  }): void => {
    enqueueSnackbar(translate('An_item_was_removed_from_your_basket'), {
      variant: 'warning',
    });
    removeItemFromBasket(payload);
  };

  const handleOnCheckout = (): void => {
    const basketTotal = BasketControl.Total(basket.items);
    const eventData = {
      amp_basketValue: basketTotal,
      amp_isLoggedin: customer.status === 'success',
      amp_label: 'Checkout Selected',
      amp_numItemsInBasket: basket.items.length,
    };
    analyticsService.track(eventData);
    const alcoholInBasket = basket.items.find((item) => item.Item.Alcohol);
    const nextPage = alcoholInBasket ? 'over18' : 'checkout';
    let nextUrl = `/${nextPage}/${vrId}/${type}/${id}${concessionStoreIdPath}`;

    if (restaurant.status === 'success' && customer.status === 'success') {
      Track('Orders', 'Checkout', {
        event_label: restaurant.data.RestaurantName,
        label: 'Checkout',
        section: 'Orders',
      });

      const isAlcoholAgeConfirmRequired =
        customer.data.RequireAgeConfirmationForAlcoholOrdersData
          .DisplayConfirmationPrompt;

      if (!isAlcoholAgeConfirmRequired) {
        nextUrl = `/checkout/${vrId}/${type}/${id}${concessionStoreIdPath}`;
      }

      urlUtils.navigateToHash(nextUrl);
    } else {
      loginAndNavigateTo();
    }
  };

  const renderOrderBasket = () => {
    if (
      basket.items.length === 0 ||
      appConfig.status !== 'success' ||
      restaurant.status !== 'success' ||
      menu === null
    ) {
      return null;
    }

    const isDeliveryOrder =
      restaurant.data.DeliveryType === Api.DeliveryType.Delivery;
    const isOutsideDeliveryZone = OutsideDeliveryZone(restaurant);
    const { buttonLabel, isCheckoutEnabled } =
      restaurantMenuUtils.getButtonState(
        restaurant.data.AcceptPreOrders,
        isDeliveryOrder,
        isOutsideDeliveryZone,
        restaurant.data.IsOpen,
        translate,
      );

    const basketActions: BasketActionsType = {
      onAddItem: (basketItem) => {
        const eventData = {
          amp_itemAddedFrom: 'Basket',
          amp_itemCount: 1,
          amp_itemName: basketItem.Item.Name,
          amp_itemPrice: basketItem.Item.Price,
          amp_label: 'Item Added to Basket',
          amp_productId: basketItem.Item.MenuItemId.toString(),
          eventLabel: basketItem.Item.Name,
          itemType: basketItem.Options.length === 0 ? 'simple' : 'complex',
          label: 'Add Item To Basket Via Basket',
          menuId: menu.MenuId,
          name: basketItem.Item.Name,
          section: 'Orders',
          virtualRestaurantId: vrId,
        };
        Track('Orders', 'Add Item To Basket Via Basket', eventData);
        addItemToBasket(basketItem);
      },
      onCheckout: () => {
        handleOnCheckout();
      },
      onRemoveItem: (basketItem) => {
        const eventData = {
          amp_itemName: basketItem.indexName,
          amp_itemPrice: 0,
          amp_itemRemovedFrom: 'Basket',
          amp_label: 'Item Removed from Basket',
          eventLabel: basketItem.indexName,
          label: 'Remove Item From Basket Via Basket',
          menuId: basketItem.menuId,
          section: 'Orders',
          virtualRestaurantId: vrId,
        };
        Track('Orders', 'Remove Item From Basket Via Basket', eventData);
        removeItemFromBasket(basketItem);
      },
    };

    const floatingBasketStyles = clsx(
      classes.floatingBasket,
      !!menu?.DisplaySectionLinks && 'hasNavigation',
    );

    return (
      <Basket
        actions={basketActions}
        basketItemClassName="basketItems"
        basketItems={basket.items}
        buttonLabel={buttonLabel}
        className={floatingBasketStyles}
        currency={restaurant.data.Currency}
        isCheckoutEnabled={isCheckoutEnabled}
        languageCode={languageCode}
        menu={menu}
        minOrder={restaurant.data.MinimumOrder}
        translate={translate}
      />
    );
  };

  const checkoutButton = useMemo(() => {
    if (
      basket.items.length === 0 ||
      appConfig.status !== 'success' ||
      restaurant.status !== 'success' ||
      menu === null
    ) {
      return null;
    }

    const isDeliveryOrder =
      restaurant.data.DeliveryType === Api.DeliveryType.Delivery;
    const isOutsideDeliveryZone = OutsideDeliveryZone(restaurant);
    const { buttonLabel, isCheckoutEnabled } =
      restaurantMenuUtils.getButtonState(
        restaurant.data.AcceptPreOrders,
        isDeliveryOrder,
        isOutsideDeliveryZone,
        restaurant.data.IsOpen,
        translate,
      );

    const basketActions = {
      onAddItem: addItemToBasket,
      onCheckout: handleOnCheckout,
      onRemoveItem: removeItemFromBasket,
    };

    return (
      <CheckoutButton
        actions={basketActions}
        basketItems={basket.items}
        buttonLabel={buttonLabel}
        currency={restaurant.data.Currency}
        languageCode={languageCode}
        isCheckoutEnabled={isCheckoutEnabled}
        minOrder={restaurant.data.MinimumOrder}
        translate={translate}
        vrId={vrId}
      />
    );
  }, [
    appConfig.status,
    basket.items,
    menu,
    restaurant.status,
    customer.status,
  ]);

  const loginAndNavigateTo = () => {
    const locationHash = urlUtils.getWindowLocation('hash');
    let loginUrl = locationHash.substring(1);

    loginUrl = loginUrl.replace('restaurant', 'checkout');
    pushLoginUrl(loginUrl);

    const redirectUrl = type === 'delivery' ? `/phone/${id}` : 'phone';
    urlUtils.navigateToHash(redirectUrl);
  };

  const getBacklinkUrl = (): string => {
    if (appConfig.status !== 'success') {
      return '/where';
    }

    const {
      DisplayPickupRestaurantListScreen: displayPickupRestaurantListScreen,
      DisplayDeliveryRestaurantListScreen: displayDeliveryRestaurantListScreen,
    } = appConfig.data;

    const isCollection = type === 'collection';
    const isDelivery = type === 'delivery';

    const skipStoreCollectionList =
      isCollection && !displayPickupRestaurantListScreen;
    const skipStoreDeliveryList =
      isDelivery && !displayDeliveryRestaurantListScreen;

    if (skipStoreCollectionList) {
      return '/where';
    }

    if (skipStoreDeliveryList) {
      return '/delivery';
    }

    if (isCollection) {
      return '/collection';
    }

    const deliveryLocationId = params?.id;
    if (isDelivery && deliveryLocationId) {
      return `/delivery/${deliveryLocationId}`;
    }

    return '/delivery';
  };

  const trackMenuItemSelected = (item): void => {
    const eventData = {
      amp_hasDescription: !!item.Description,
      amp_hasImage: !!item.ImageUrl,
      amp_hasModifiers: !!item.MenuItemOptionSets.length,
      amp_itemName: item.Name,
      amp_itemPrice: item.Price,
      amp_label: 'Menu Item Selected',
      amp_productId: item.MenuItemId.toString(),
    };
    analyticsService.track(eventData);
  };

  const getRestaurantName = (configData): string => {
    if (restaurant.status !== 'success') {
      const { Category: category } = configData;

      const categoryName =
        restaurantMenuUtils.getCategoryTranslationKey(category);
      const loadingMessage = translate('Loading_category_name', {
        categoryName: translate(categoryName) as string,
      });

      return `${loadingMessage}...`;
    }

    return restaurant.data.RestaurantName;
  };

  const handleClickItem = (payload: { menuId: number; item: Api.MenuItem }) => {
    if (restaurant.status !== 'success') {
      console.error('Cannot click - restaurant is', restaurant.status, props);
      return;
    }

    if (!restaurant.data.IsOpen && !restaurant.data.AcceptPreOrders) {
      const openAt = Utilities.getOpeningTime(restaurant.data);
      // eslint-disable-next-line no-alert
      alert(
        translate('Sorry_{restaurantName}_is_closed_It_will_open_at_{openAt}', {
          restaurantName: restaurant.data.RestaurantName,
          openAt,
        }),
      );
      return;
    }

    const { item } = payload;
    if (menuHasConcessions) {
      const concessionItem = Object.assign(item, {
        ConcessionStoreId: concessionStoreId,
      });
      setSelectedMenuOption(concessionItem);
    }
    setSelectedMenuOption(item);

    const hasMenuItemOptionSets = item.MenuItemOptionSets.length > 0;
    if (hasMenuItemOptionSets) {
      trackMenuItemSelected(item);
      setShowMenuOptionsDialog(true);
    } else {
      trackMenuItemSelected(item);
      setShowMenuDetailDialog(true);
    }
  };

  if (appConfig.status !== 'success' || restaurant.status !== 'success') {
    return <LoadingSpinner />;
  }

  const backlinkUrl = getBacklinkUrl();

  const trackBackSelected = (): void => {
    const eventData = {
      amp_label: 'Back Selected',
      amp_view: 'Restaurant Menu',
    };
    analyticsService.track(eventData);
  };

  const currency = restaurant.data.Currency
    ? restaurant.data.Currency
    : Api.Currency.EUR;
  const isDeliveryOrder =
    restaurant.data.DeliveryType === Api.DeliveryType.Delivery;
  const isOutsideDeliveryZone = OutsideDeliveryZone(restaurant);

  const menuActions: MenuActionsType = {
    onAddExpanded: addToExpandedState,
    onAddItem: handleOnAddItem,
    initExpanded: initializeExpandedState,
    onItemClick: handleClickItem,
    onRemoveExpanded: removeFromExpandedState,
    onRemoveItem: handleOnRemoveItem,
  };

  // TODO - Switch over isTableServiceOnly & isCollectionRestaurantOnly to selectors to avoid rechecks every render
  const isTableServiceOnly = !isDeliveryOrder
    ? // @ts-ignore ICH+2021.11.05 how to differentiate typescript union type?
      checkIsTableServiceOnly(restaurant.data)
    : false;

  const isCollectionRestaurantOnly = !isDeliveryOrder
    ? checkIsCollectionRestaurantOnly(appConfig.data, restaurants)
    : false;

  const showBackLinkButton = !isTableServiceOnly && !isCollectionRestaurantOnly;

  const basketStyles = clsx('fd-content__side', classes.basket);
  return (
    <>
      <Page pageName="restaurant-menu">
        <PageHeader
          showProfileMenu
          title={getRestaurantName(appConfig.data)}
          params={{
            id: id?.toString(),
            type,
            vrid: vrId.toString(),
          }}
        >
          {showBackLinkButton && (
            <BackLink
              label={translate('Back')}
              linkPath={backlinkUrl}
              onClick={trackBackSelected}
              testId="restaurant-back"
            />
          )}
        </PageHeader>

        <article className="fd-content__main">
          <StoreHeader
            currency={currency}
            deliveryLocation={deliveryLocation}
            hasLockedTableNumber={hasLockedTableNumber}
            languageCode={languageCode}
            prId={id}
            restaurant={restaurant.data}
            setTableLock={setTableLock}
            tableNumber={basket.tableNumber}
            translate={translate}
            type={type}
            vrId={vrId}
          />
          <NotAvailableWarning
            isDeliveryOrder={isDeliveryOrder}
            isOutsideDeliveryZone={isOutsideDeliveryZone}
            restaurantData={restaurant.data}
            translate={translate}
          />
          <Menu
            actions={menuActions}
            configData={appConfig.data}
            basket={basket}
            currency={currency}
            languageCode={languageCode}
            expandedState={expandedState}
            menuData={menu}
            params={params}
            restaurantData={restaurant.data}
            translate={translate}
            viewportTopOffset={viewportTopOffset}
          />
        </article>

        <section className={basketStyles}>
          <ProfilePanel
            params={{
              id: id?.toString(),
              type,
              vrid: vrId.toString(),
            }}
          />

          <Hidden mdDown>{renderOrderBasket()}</Hidden>
        </section>
      </Page>

      <Hidden lgUp>{checkoutButton}</Hidden>

      {customer.status === 'success' && !!customer.data.EmailAddress && (
        <CampaignConsentDialog customer={customer} />
      )}

      {showMenuDetailDialog && (
        <MenuDetailDialog
          currency={currency}
          item={selectedMenuItem}
          languageCode={languageCode}
          menuId={selectedMenuItem.MenuId}
          onAddItem={addItemToBasket}
          setShowDialog={setShowMenuDetailDialog}
          showDialog={showMenuDetailDialog}
          translate={translate}
          vrId={vrId}
        />
      )}

      {showMenuOptionsDialog && (
        <MenuOptionsDialog
          currency={currency}
          item={selectedMenuItem}
          languageCode={languageCode}
          menuId={selectedMenuItem.MenuId}
          onAddItem={addItemToBasket}
          setShowDialog={setShowMenuOptionsDialog}
          showDialog={showMenuOptionsDialog}
          translate={translate}
          vrId={vrId}
        />
      )}
    </>
  );
}
